import { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../axiosInstance';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  AreaChart,
  Area,
  CartesianGrid,
} from 'recharts';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import {
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  Maximize2,
  Minimize2,
  Search,
} from 'lucide-react';
import { MapContainer, GeoJSON, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const columns = [
  { header: 'State', accessorKey: 'name' },
  { header: 'Total Qty (MT)', accessorKey: 'totalQty' },
  { header: 'Blocked Qty (MT)', accessorKey: 'blockedQty' },
  { header: 'Available Qty (MT)', accessorKey: 'availableQty' },
];

// State name to filename mapping
const STATE_GEOJSON_MAP = {
  'andaman and nicobar islands': 'andaman-and-nicobar-islands.geojson',
  'andhra pradesh': 'andhra-pradesh.geojson',
  'arunachal pradesh': 'arunachal-pradesh.geojson',
  assam: 'assam.geojson',
  bihar: 'bihar.geojson',
  chandigarh: 'chandigarh.geojson',
  chhattisgarh: 'chhattisgarh.geojson',
  'dadra & nagar haveli': 'dnh-and-dd.geojson',
  'daman & diu': 'dnh-and-dd.geojson',
  delhi: 'delhi.geojson',
  goa: 'goa.geojson',
  gujarat: 'gujarat.geojson',
  haryana: 'haryana.geojson',
  'himachal pradesh': 'himachal-pradesh.geojson',
  'jammu and kashmir': 'jammu-and-kashmir.geojson',
  jharkhand: 'jharkhand.geojson',
  karnataka: 'karnataka.geojson',
  kerala: 'kerala.geojson',
  lakshadweep: 'lakshadweep.geojson',
  'madhya pradesh': 'madhya-pradesh.geojson',
  maharashtra: 'maharashtra.geojson',
  manipur: 'manipur.geojson',
  meghalaya: 'meghalaya.geojson',
  mizoram: 'mizoram.geojson',
  nagaland: 'nagaland.geojson',
  odisha: 'odisha.geojson',
  puducherry: 'puducherry.geojson',
  punjab: 'punjab.geojson',
  rajasthan: 'rajasthan.geojson',
  sikkim: 'sikkim.geojson',
  'tamil nadu': 'tamil-nadu.geojson',
  telangana: 'telangana.geojson',
  tripura: 'tripura.geojson',
  'uttar pradesh': 'uttar-pradesh.geojson',
  uttarakhand: 'uttarakhand.geojson',
  'west bengal': 'west-bengal.geojson',
};

// Color interpolation helper for continuous gradient
const interpolateRgb = (rgb1, rgb2, factor) => {
  const r = Math.round(rgb1[0] + factor * (rgb2[0] - rgb1[0]));
  const g = Math.round(rgb1[1] + factor * (rgb2[1] - rgb1[1]));
  const b = Math.round(rgb1[2] + factor * (rgb2[2] - rgb1[2]));
  return `rgb(${r}, ${g}, ${b})`;
};

// Continuous color gradient from light blue (#dbeafe) -> royal blue (#2563eb) -> deep navy (#0f172a) (darker for more)
const getContinuousGradientColor = (count, maxCount) => {
  const c = Number(count) || 0;
  if (c <= 0 || !maxCount) {
    return {
      color: '#f8fafc', // Very subtle slate for zero warehouses
      fillOpacity: 0.55,
    };
  }

  // Normalized ratio (0 to 1)
  const ratio = Math.min(Math.max(c / maxCount, 0), 1);

  // Smooth gradient stops:
  // Light Sky (219, 234, 254) -> Royal Blue (37, 99, 235) -> Deep Navy (15, 23, 42)
  let rgbColor;
  if (ratio <= 0.45) {
    rgbColor = interpolateRgb([219, 234, 254], [37, 99, 235], ratio / 0.45);
  } else {
    rgbColor = interpolateRgb([37, 99, 235], [15, 23, 42], (ratio - 0.45) / 0.55);
  }

  const fillOpacity = 0.7 + ratio * 0.25; // 0.70 to 0.95
  return { color: rgbColor, fillOpacity };
};

const normalizeStateName = (name) => {
  if (!name) return '';
  return name
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/\bnct of delhi\b/g, 'delhi')
    .replace(/\borissa\b/g, 'odisha')
    .replace(/\bpoducherry\b/g, 'puducherry')
    .replace(/\bpondicherry\b/g, 'puducherry')
    .replace(/\buttaranchal\b/g, 'uttarakhand')
    .replace(/[^a-z0-9]/g, '')
    .trim();
};

// Helper function to parse quantity strings like "2174 MT" to number
const parseQty = (value) => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const num = parseFloat(value.replace(/\s*MT\s*/, ''));
    return isNaN(num) ? 0 : num;
  }
  return 0;
};

const Dashboard = () => {
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tableData, setTableData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [stateSearchText, setStateSearchText] = useState('');
  const pageSize = 8;
  const totalPages = Math.ceil(tableData.length / pageSize);

  const [tableLoading, setTableLoading] = useState(true);
  const [barData, setBarData] = useState([]);
  const [barLoading, setBarLoading] = useState(true);
  const [oilSeedData, setOilSeedData] = useState([]);
  const [oilSeedLoading, setOilSeedLoading] = useState(true);
  const [geoData, setGeoData] = useState(null);
  const [warehouseCountData, setWarehouseCountData] = useState([]);
  const [warehouseCountLoading, setWarehouseCountLoading] = useState(true);
  const [agedWarehousesData, setAgedWarehousesData] = useState([]);
  const [agedWarehousesLoading, setAgedWarehousesLoading] = useState(true);
  const [warehousesList, setWarehousesList] = useState([]);

  // Aged warehouses pagination and search
  const [agedCurrentPage, setAgedCurrentPage] = useState(1);
  const [agedSearchText, setAgedSearchText] = useState('');
  const [agedStateFilter, setAgedStateFilter] = useState('');
  const agedPageSize = 8;
  const agedTotalPages = Math.ceil(agedWarehousesData.length / agedPageSize);

  // New state for filtering
  const [selectedState, setSelectedState] = useState('');
  const [statesList, setStatesList] = useState([]);
  const [mapLoading, setMapLoading] = useState(false);

  // State for warehouse locations
  const [warehouseLocations, setWarehouseLocations] = useState([]);
  const [warehouseLocationsLoading, setWarehouseLocationsLoading] =
    useState(false);

  // State for fullscreen map
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);
  const [mapKey, setMapKey] = useState(0);
  const [allStatesStock, setAllStatesStock] = useState([]);
  const [allStatesStockLoading, setAllStatesStockLoading] = useState(false);
  const [expandedSearchText, setExpandedSearchText] = useState('');

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalBarData, setModalBarData] = useState([]);

  const [modalLoading, setModalLoading] = useState(false);

  // Seasons state
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState('');

  // Error toast state
  const [errorToast, setErrorToast] = useState({ show: false, message: '' });

  // User role and state for branch users
  const [role, setRole] = useState('');
  const [userStateId, setUserStateId] = useState('');

  // Get user role and state from localStorage
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    const userRole = user?.roles?.[0]?.name || '';
    const stateId = user?.state?.id || '';
    setRole(userRole);
    setUserStateId(stateId);
  }, []);

  // Show error toast
  const showError = (message) => {
    setErrorToast({ show: true, message });
    setTimeout(() => {
      setErrorToast({ show: false, message: '' });
    }, 5000);
  };

  // Fetch seasons
  useEffect(() => {
    const fetchSeasons = async () => {
      try {
        const res = await axiosInstance.get('seasons?paginate=false');
        const seasonsData = Array.isArray(res.data) ? res.data : res.data?.data;
        if (Array.isArray(seasonsData)) {
          setSeasons(seasonsData);
        }
      } catch (err) {
        const errorMessage = err.response?.data?.message || 'Failed to fetch seasons';
        console.error('Error fetching seasons:', err);
        showError(errorMessage);
      }
    };
    fetchSeasons();
  }, []);

  // Fetch dashboard stats
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const params = {
          ...(selectedSeason && { seasonId: selectedSeason }),
          ...(selectedState && { stateId: selectedState }),
        };
        const res = await axiosInstance.get('dashboard', { params });
        if (res.data?.success) {
          setStats(res.data.data);
        } else {
          const errorMessage = res.data?.message || 'Failed to fetch dashboard data';
          showError(errorMessage);
        }
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch dashboard data';
        console.error('Error fetching dashboard data:', err);
        showError(errorMessage);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, [selectedSeason, selectedState]);

  // Fetch states for filter dropdown
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const res = await axiosInstance.get('states');
        if (res.data && Array.isArray(res.data)) {
          setStatesList(res.data);
          // Auto-select user's state for Branch role
          if (role === 'Branch' && userStateId) {
            setSelectedState(userStateId);
          }
        } else {
          showError('Invalid states data format');
        }
      } catch (err) {
        const errorMessage = err.response?.data?.message || 'Failed to fetch states';
        console.error('Error fetching states:', err);
        showError(errorMessage);
      }
    };
    fetchStates();
  }, [role, userStateId]);

  // Fetch warehouses for filter dropdown
  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        const res = await axiosInstance.get('warehouse');
        if (res.data?.data && Array.isArray(res.data.data)) {
          setWarehousesList(res.data.data);
        } else {
          showError('Invalid warehouses data format');
        }
      } catch (err) {
        const errorMessage = err.response?.data?.message || 'Failed to fetch warehouses';
        console.error('Error fetching warehouses:', err);
        showError(errorMessage);
      }
    };
    fetchWarehouses();
  }, []);

  // Fetch table data
  useEffect(() => {
    const fetchTableData = async () => {
      try {
        setTableLoading(true);
        const params = {
          ...(selectedSeason && { seasonId: selectedSeason }),
          ...(selectedState && { stateId: selectedState }),
          ...(!selectedState && stateSearchText && { stateId: stateSearchText })
        };
        const res = await axiosInstance.get('dashboard/state-wise-stock', { params });
        if (res.data?.success) {
          const mappedData = res.data.data.map((item, index) => ({
            id: index + 1,
            name: item.stateName,
            stateId: item.stateId,
            totalQty: item.totalQty,
            blockedQty: item.blockedQty,
            availableQty: item.availableQty,
          }));
          setTableData(mappedData);
          setCurrentPage(1); // Reset to first page on search
        } else {
          const errorMessage = res.data?.message || 'Failed to fetch state-wise stock data';
          showError(errorMessage);
        }
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch state-wise stock data';
        console.error('Error fetching state-wise stock:', err);
        showError(errorMessage);
      } finally {
        setTableLoading(false);
      }
    };
    fetchTableData();
  }, [selectedSeason, selectedState, stateSearchText]);

  // Fetch bar chart data (other)
  useEffect(() => {
    const fetchBarData = async () => {
      try {
        setBarLoading(true);
        const params = {
          ...(selectedSeason && { seasonId: selectedSeason }),
          ...(selectedState && { stateId: selectedState }),
        };
        const res = await axiosInstance.get(
          'dashboard/commodity-wise-stock/other',
          { params }
        );
        if (res.data?.success) {
          const mappedData = res.data.data.map((item) => ({
            name: item.commodityName,
            totalQty: item.totalQty,
            totalBlockedQty: item.totalBlockedQty || 0,
          }));
          setBarData(mappedData);
        } else {
          const errorMessage = res.data?.message || 'Failed to fetch commodity-wise stock data';
          showError(errorMessage);
        }
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch commodity-wise stock data';
        console.error('Error fetching commodity-wise stock:', err);
        showError(errorMessage);
      } finally {
        setBarLoading(false);
      }
    };
    fetchBarData();
  }, [selectedSeason, selectedState]);

  // Fetch bar chart data (oil-seed)
  useEffect(() => {
    const fetchOilSeedData = async () => {
      try {
        setOilSeedLoading(true);
        const params = {
          ...(selectedSeason && { seasonId: selectedSeason }),
          ...(selectedState && { stateId: selectedState }),
        };
        const res = await axiosInstance.get(
          'dashboard/commodity-wise-stock/oil-seed',
          { params }
        );
        if (res.data?.success) {
          const mappedData = res.data.data.map((item) => ({
            name: item.commodityName,
            totalQty: item.totalQty,
            totalBlockedQty: item.totalBlockedQty || 0,
          }));
          setOilSeedData(mappedData);
        } else {
          const errorMessage = res.data?.message || 'Failed to fetch oil-seed stock data';
          showError(errorMessage);
        }
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch oil-seed stock data';
        console.error('Error fetching oil-seed stock:', err);
        showError(errorMessage);
      } finally {
        setOilSeedLoading(false);
      }
    };
    fetchOilSeedData();
  }, [selectedSeason, selectedState]);

  // Fetch warehouse count data
  useEffect(() => {
    const fetchWarehouseCount = async () => {
      try {
        setWarehouseCountLoading(true);
        const res = await axiosInstance.get(
          'dashboard/state-wise-warehouse-count'
        );
        if (res.data?.success) {
          setWarehouseCountData(res.data.data);
        } else {
          const errorMessage = res.data?.message || 'Failed to fetch warehouse count data';
          showError(errorMessage);
        }
      } catch (err) {
        const errorMessage = err.response?.data?.message || 'Failed to fetch warehouse count data';
        console.error('Error fetching warehouse count:', err);
        showError(errorMessage);
      } finally {
        setWarehouseCountLoading(false);
      }
    };
    fetchWarehouseCount();
  }, []);

  // Fetch aged warehouses data
  useEffect(() => {
    const fetchAgedWarehouses = async () => {
      try {
        setAgedWarehousesLoading(true);
        const params = {
          ...(selectedSeason && { seasonId: selectedSeason }),
          ...(agedStateFilter && { stateId: agedStateFilter }),
          ...(agedSearchText && { warehouseId: agedSearchText })
        };
        const res = await axiosInstance.get('dashboard/get-aged-warehouses', { params });
        if (res.data?.success) {
          setAgedWarehousesData(res.data.data);
          setAgedCurrentPage(1); // Reset to first page on search
        } else {
          const errorMessage = res.data?.message || 'Failed to fetch aged warehouses data';
          showError(errorMessage);
        }
      } catch (err) {
        const errorMessage = err.response?.data?.message || 'Failed to fetch aged warehouses data';
        console.error('Error fetching aged warehouses:', err);
        showError(errorMessage);
      } finally {
        setAgedWarehousesLoading(false);
      }
    };
    fetchAgedWarehouses();
  }, [selectedSeason, agedSearchText, agedStateFilter]);

  // Fetch warehouse locations when state is selected
  useEffect(() => {
    const fetchWarehouseLocations = async () => {
      if (!selectedState) {
        setWarehouseLocations([]);
        return;
      }

      try {
        setWarehouseLocationsLoading(true);
        const res = await axiosInstance.get(
          'dashboard/state-wise-warehouse-with-stock',
          {
            params: {
              stateId: selectedState,
              ...(selectedSeason && { seasonId: selectedSeason }),
            },
          }
        );

        if (res.data?.success && Array.isArray(res.data.data)) {
          const validLocations = res.data.data
            .filter(
              (wh) =>
                wh.latitude &&
                wh.longitude &&
                wh.latitude !== '0' &&
                wh.longitude !== '0' &&
                !isNaN(parseFloat(wh.latitude)) &&
                !isNaN(parseFloat(wh.longitude))
            )
            .map((wh) => ({
              id: wh.warehouseId,
              name: wh.warehouseName,
              totalQty: wh.totalQty || 0,
              latitude: parseFloat(wh.latitude),
              longitude: parseFloat(wh.longitude),
            }));

          setWarehouseLocations(validLocations);
        } else {
          setWarehouseLocations([]);
        }
      } catch (err) {
        const errorMessage = err.response?.data?.message || 'Failed to fetch warehouse locations';
        console.error('Error fetching warehouse locations:', err);
        showError(errorMessage);
        setWarehouseLocations([]);
      } finally {
        setWarehouseLocationsLoading(false);
      }
    };

    fetchWarehouseLocations();
  }, [selectedState, selectedSeason]);

  // Fetch all states stock for the expanded map view
  useEffect(() => {
    const fetchAllStatesStock = async () => {
      try {
        setAllStatesStockLoading(true);
        const params = {
          ...(selectedSeason && { seasonId: selectedSeason }),
        };
        const res = await axiosInstance.get('dashboard/state-wise-stock', { params });
        if (res.data?.success && Array.isArray(res.data.data)) {
          setAllStatesStock(res.data.data);
        }
      } catch (err) {
        console.error('Error fetching all states stock:', err);
      } finally {
        setAllStatesStockLoading(false);
      }
    };
    fetchAllStatesStock();
  }, [selectedSeason]);

  // Helper function to get selected state name from ID
  const getSelectedStateName = () => {
    if (!selectedState) return '';
    const state = statesList.find((s) => s.id === selectedState);
    return state ? state.name : '';
  };

  // Load GeoJSON based on selected state
  useEffect(() => {
    const loadGeoJSON = async () => {
      setMapLoading(true);
      try {
        let geoJsonPath;

        if (selectedState) {
          const stateName = getSelectedStateName();
          // Check if state has a specific GeoJSON file
          const stateFileName = STATE_GEOJSON_MAP[stateName.toLowerCase()];
          if (stateFileName) {
            geoJsonPath = `/${stateFileName}`;
          } else {
            // Fallback to India map if state file doesn't exist
            console.warn(
              `GeoJSON file not found for ${stateName}. Showing India map.`
            );
            geoJsonPath = '/simple-geoBoundaries-IND-ADM1.geojson';
          }
        } else {
          // Load India map by default
          geoJsonPath = '/simple-geoBoundaries-IND-ADM1.geojson';
        }

        const response = await fetch(geoJsonPath);
        if (!response.ok) {
          throw new Error(`Failed to load GeoJSON: ${response.status}`);
        }
        const data = await response.json();
        setGeoData(data);
      } catch (err) {
        console.error('Error loading GeoJSON:', err);
        // Fallback to India map on error
        if (selectedState) {
          // console.log('Falling back to India map...');
          try {
            const response = await fetch(
              '/simple-geoBoundaries-IND-ADM1.geojson'
            );
            const data = await response.json();
            setGeoData(data);
          } catch (fallbackErr) {
            console.error('Error loading fallback GeoJSON:', fallbackErr);
          }
        }
      } finally {
        setMapLoading(false);
      }
    };

    loadGeoJSON();
  }, [selectedState, statesList]);

  const data = useMemo(() => tableData, [tableData]);
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const maxWarehouseCount = useMemo(() => {
    if (!warehouseCountData || !warehouseCountData.length) return 1;
    const max = Math.max(
      ...warehouseCountData.map((item) => Number(item.warehouseCount) || 0),
      1
    );
    return max;
  }, [warehouseCountData]);

  const getWarehouseCount = (stateName) => {
    if (!stateName || !warehouseCountData) return 0;
    const norm = normalizeStateName(stateName);
    const state = warehouseCountData.find(
      (item) => normalizeStateName(item.stateName) === norm
    );
    return state ? Number(state.warehouseCount) || 0 : 0;
  };

  // Get total quantity for a state from allStatesStock or tableData
  const getStateTotalQty = (stateName) => {
    if (!stateName) return 0;
    const norm = normalizeStateName(stateName);
    const stock = allStatesStock.find(
      (item) => normalizeStateName(item.stateName) === norm
    );
    if (stock) return parseFloat(stock.totalQty || 0);

    const tableStock = tableData.find(
      (item) => normalizeStateName(item.name) === norm
    );
    if (tableStock) return parseFloat(tableStock.totalQty || 0);

    return 0;
  };

  const getGeoJSONStyle = (feature) => {
    if (selectedState) {
      return {
        color: '#334155',
        weight: 1.2,
        opacity: 0.85,
        fillOpacity: 0.65,
        fillColor: '#93c5fd',
        lineJoin: 'round',
        lineCap: 'round',
      };
    }

    const stateName =
      feature?.properties?.shapeName || feature?.properties?.district || '';
    const warehouseCount = getWarehouseCount(stateName);
    const style = getContinuousGradientColor(warehouseCount, maxWarehouseCount);

    return {
      color: '#334155',
      weight: 1.2,
      opacity: 0.85,
      fillOpacity: style.fillOpacity,
      fillColor: style.color,
      lineJoin: 'round',
      lineCap: 'round',
    };
  };

  const onEachState = (feature, layer) => {
    const displayName =
      feature.properties.shapeName || feature.properties.district || 'Unknown';
    const isState = !!feature.properties.shapeName;
    const warehouseCount = isState ? getWarehouseCount(displayName) : null;
    const totalQty = isState ? getStateTotalQty(displayName) : null;

    // Create a popup content string (for hover)
    const popupContent = `
    <div style="text-align: center; padding: 6px 8px; font-family: ui-sans-serif, system-ui, sans-serif;">
      <strong style="font-size: 13px; color: #0f172a; font-weight: 700;">${displayName}</strong>
      <div style="margin-top: 4px; font-size: 11px; line-height: 1.4;">
        ${
          warehouseCount !== null
            ? `<div style="color: #2563eb; font-weight: 600;">Warehouses: <strong style="color: #1e3a8a;">${warehouseCount}</strong></div>`
            : ''
        }
        ${
          totalQty !== null
            ? `<div style="color: #475569; font-size: 10.5px; margin-top: 2px;">Total Stock: <strong>${totalQty.toLocaleString(
                'en-IN',
                { minimumFractionDigits: 1, maximumFractionDigits: 1 }
              )} MT</strong></div>`
            : ''
        }
      </div>
    </div>
  `;

    // --- Mouse interaction handlers ---
    layer.on({
      mouseover: (e) => {
        const target = e.target;
        target.bringToFront();
        target.setStyle({
          weight: 2.2,
          color: '#0f172a',
          fillOpacity: 0.95,
        });

        // Open popup on hover
        layer
          .bindPopup(popupContent, { closeButton: false })
          .openPopup(e.latlng);
      },
      mouseout: (e) => {
        const target = e.target;
        if (selectedState) {
          target.setStyle({
            weight: 1.2,
            color: '#334155',
            opacity: 0.85,
            fillOpacity: 0.65,
            fillColor: '#93c5fd',
          });
        } else {
          const warehouseCount = getWarehouseCount(displayName);
          const origStyle = getContinuousGradientColor(warehouseCount, maxWarehouseCount);
          target.setStyle({
            weight: 1.2,
            color: '#334155',
            opacity: 0.85,
            fillOpacity: origStyle.fillOpacity,
            fillColor: origStyle.color,
          });
        }

        // Close popup when mouse leaves
        layer.closePopup();
      },
      click: () => {
        // --- Handle click to filter map ---
        if (isState) {
          // Find the selected state from list (case insensitive match)
          const normDisplayName = normalizeStateName(displayName);
          const selected = statesList.find(
            (s) => normalizeStateName(s.name) === normDisplayName
          );
          if (selected) {
            setSelectedState(selected.id);
          } else {
            console.warn(`State not found in dropdown list: ${displayName}`);
          }
        }
      },
    });
  };

  // Calculate map center and zoom based on selected state
  const getMapConfig = () => {
    if (!selectedState) {
      return { center: [22.5, 82.5], zoom: 4 }; // India center
    }

    const stateName = getSelectedStateName().toLowerCase();

    // State-specific centers
    const stateCenters = {
      'andaman and nicobar islands': { center: [10.3482, 93.0479], zoom: 6 },
      'andhra pradesh': { center: [15.8165, 80.1767], zoom: 6 },
      'arunachal pradesh': { center: [27.9196, 94.7167], zoom: 7 },
      assam: { center: [26.1851, 92.6983], zoom: 7 },
      bihar: { center: [25.7923, 85.585], zoom: 7 },
      chandigarh: { center: [30.7311, 76.7805], zoom: 9 },
      chhattisgarh: { center: [21.2746, 82.0685], zoom: 7 },
      'dadra & nagar haveli': { center: [20.2116, 72.2624], zoom: 9 },
      'daman & diu': { center: [20.2116, 72.2624], zoom: 9 },
      delhi: { center: [28.6366, 77.1093], zoom: 9 },
      goa: { center: [15.377, 74.0425], zoom: 8 },
      gujarat: { center: [22.5996, 71.5831], zoom: 7 },
      haryana: { center: [29.296, 76.3138], zoom: 7 },
      'himachal pradesh': { center: [31.882, 77.2989], zoom: 7 },
      'jammu and kashmir': { center: [33.6466, 74.885], zoom: 7 },
      jharkhand: { center: [23.6787, 85.6948], zoom: 7 },
      karnataka: { center: [14.8367, 76.3961], zoom: 7 },
      kerala: { center: [10.546, 76.3744], zoom: 7 },
      lakshadweep: { center: [10.2628, 72.9097], zoom: 8 },
      'madhya pradesh': { center: [23.6822, 78.2765], zoom: 6 },
      maharashtra: { center: [19.3541, 76.381], zoom: 7 },
      manipur: { center: [24.7502, 93.8883], zoom: 8 },
      meghalaya: { center: [25.5621, 91.3277], zoom: 8 },
      mizoram: { center: [23.2886, 92.842], zoom: 8 },
      nagaland: { center: [26.0131, 94.4252], zoom: 8 },
      odisha: { center: [20.4615, 84.3162], zoom: 7 },
      puducherry: { center: [13.1373, 79.2312], zoom: 9 },
      punjab: { center: [30.8689, 75.4043], zoom: 7 },
      rajasthan: { center: [26.5484, 73.9635], zoom: 6 },
      sikkim: { center: [27.5718, 88.4801], zoom: 8 },
      'tamil nadu': { center: [10.9312, 78.3339], zoom: 7 },
      telangana: { center: [17.803, 79.0436], zoom: 7 },
      tripura: { center: [23.7417, 91.7535], zoom: 8 },
      'uttar pradesh': { center: [26.6755, 80.4923], zoom: 6 },
      uttarakhand: { center: [30.142, 79.2095], zoom: 7 },
      'west bengal': { center: [24.2219, 88.0788], zoom: 7 },
    };

    return stateCenters[stateName] || { center: [22.5, 82.5], zoom: 4 };
  };

  const mapConfig = getMapConfig();

  // Handler for toggling fullscreen
  const toggleFullscreen = () => {
    setIsMapFullscreen(!isMapFullscreen);
    // Force re-render of map to recenter after a small delay to allow layout to settle
    setTimeout(() => {
      setMapKey((prev) => prev + 1);
    }, 100);
  };

  // Get zoom level based on fullscreen state
  const getZoomLevel = () => {
    if (isMapFullscreen) {
      return mapConfig.zoom + 1;
    }
    return mapConfig.zoom;
  };

  // Listen for Escape key to exit fullscreen
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isMapFullscreen) {
        setIsMapFullscreen(false);
        setTimeout(() => {
          setMapKey((prev) => prev + 1);
        }, 100);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMapFullscreen]);

  // Merged states table data for expanded view - ONLY states with warehouseCount > 0
  const expandedStatesTableData = useMemo(() => {
    if (!warehouseCountData || !Array.isArray(warehouseCountData)) return [];

    return warehouseCountData
      .filter((item) => Number(item.warehouseCount) > 0)
      .map((whItem) => {
        const stockMatch = allStatesStock.find(
          (st) =>
            (st.stateId && whItem.stateId && st.stateId === whItem.stateId) ||
            st.stateName?.trim().toLowerCase() === whItem.stateName?.trim().toLowerCase()
        );
        const totalQty = stockMatch ? parseFloat(stockMatch.totalQty || 0) : 0;
        const blockedQty = stockMatch ? parseFloat(stockMatch.blockedQty || 0) : 0;
        const availableQty = stockMatch ? parseFloat(stockMatch.availableQty || 0) : 0;

        return {
          stateId: whItem.stateId,
          stateName: whItem.stateName,
          warehouseCount: Number(whItem.warehouseCount) || 0,
          totalQty,
          blockedQty,
          availableQty,
        };
      })
      .sort((a, b) => b.warehouseCount - a.warehouseCount || b.totalQty - a.totalQty);
  }, [warehouseCountData, allStatesStock]);

  // Filtered by search query in expanded view
  const filteredExpandedStates = useMemo(() => {
    if (!expandedSearchText.trim()) return expandedStatesTableData;
    const query = expandedSearchText.toLowerCase().trim();
    return expandedStatesTableData.filter((item) =>
      item.stateName.toLowerCase().includes(query)
    );
  }, [expandedStatesTableData, expandedSearchText]);

  // Totals for expanded view
  const expandedTotals = useMemo(() => {
    return expandedStatesTableData.reduce(
      (acc, item) => ({
        totalStates: acc.totalStates + 1,
        totalWarehouses: acc.totalWarehouses + (item.warehouseCount || 0),
        totalQty: acc.totalQty + (item.totalQty || 0),
      }),
      { totalStates: 0, totalWarehouses: 0, totalQty: 0 }
    );
  }, [expandedStatesTableData]);

  // Map rendering helper
  const renderMapContent = () => {
    if (mapLoading || warehouseCountLoading) {
      return (
        <div className="flex flex-col items-center justify-center h-full gap-2">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <p className="text-gray-500 text-xs md:text-sm">Loading map...</p>
        </div>
      );
    }
    if (!geoData) {
      return (
        <div className="flex items-center justify-center h-full">
          <p className="text-gray-500 text-xs md:text-sm">Map data not available</p>
        </div>
      );
    }
    return (
      <div className="relative w-full h-full overflow-hidden">
        <MapContainer
          key={`${selectedState}-${mapKey}-${isMapFullscreen}-${warehouseCountData.length}-${allStatesStock.length}`}
          center={mapConfig.center}
          zoom={getZoomLevel()}
          style={{
            height: '100%',
            width: '100%',
            borderRadius: '8px',
            backgroundColor: '#f8fafc',
          }}
          zoomControl={true}
          scrollWheelZoom={true}
          doubleClickZoom={true}
          dragging={true}
          minZoom={1}
          maxZoom={20}
          attributionControl={false}
        >
          <GeoJSON
            key={`geojson-${selectedState}-${warehouseCountData.length}-${allStatesStock.length}`}
            data={geoData}
            style={getGeoJSONStyle}
            onEachFeature={onEachState}
          />

          {/* Warehouse Markers - only show when state is selected */}
          {selectedState &&
            warehouseLocations.map((warehouse) => (
              <Marker
                key={warehouse.id}
                position={[warehouse.latitude, warehouse.longitude]}
              >
                <Popup>
                  <div style={{ maxWidth: '200px' }}>
                    <strong style={{ fontSize: '14px' }}>
                      {warehouse.name}
                    </strong>
                    <p
                      style={{
                        fontSize: '12px',
                        marginTop: '4px',
                        color: '#555',
                      }}
                    >
                      Available Quantity:{' '}
                      <strong>{warehouse.totalQty.toFixed(1)} MT</strong>
                    </p>
                  </div>
                </Popup>
              </Marker>
            ))}
        </MapContainer>

        {/* Continuous Gradient Legend on India Map */}
        {!selectedState && (
          <div className="absolute bottom-3 left-3 z-[400] bg-white/95 dark:bg-gray-800/95 backdrop-blur-md px-3.5 py-2.5 rounded-xl shadow-md border border-gray-200/80 dark:border-gray-700 text-gray-800 dark:text-gray-200 pointer-events-auto">
            <div className="flex items-center justify-between gap-3 mb-1.5">
              <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-gray-600 dark:text-gray-400 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                Warehouse Density
              </p>
              <span className="text-[9px] text-gray-400 dark:text-gray-500 font-medium">Gradient</span>
            </div>

            {/* Continuous Gradient Bar */}
            <div className="flex flex-col gap-1 min-w-[200px] sm:min-w-[240px]">
              <div
                className="h-2.5 rounded-md border border-gray-300/80 dark:border-gray-600 shadow-inner"
                style={{
                  background: 'linear-gradient(to right, #f8fafc 0%, #dbeafe 15%, #2563eb 60%, #0f172a 100%)',
                }}
              />
              <div className="flex items-center justify-between text-[9px] font-semibold text-gray-500 dark:text-gray-400 px-0.5 mt-0.5">
                <span>0 (Fewest)</span>
                <span>Max ({maxWarehouseCount})</span>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-3 md:space-y-4 overflow-hidden">
      {/* Error Toast Notification */}
      {errorToast.show && (
        <div className="fixed top-4 right-4 z-50 animate-fade-in">
          <div className="bg-red-500 text-white px-6 py-4 rounded-lg shadow-2xl flex items-start gap-3 max-w-md">
            <svg className="w-6 h-6 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <p className="font-semibold text-sm">Error</p>
              <p className="text-sm mt-1">{errorToast.message}</p>
            </div>
            <button
              onClick={() => setErrorToast({ show: false, message: '' })}
              className="flex-shrink-0 hover:bg-red-600 rounded p-1 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      <div className="h-0"></div>

      {/* === Stat Cards === */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 overflow-hidden min-w-0">
        {loading ? (
          <p className="text-gray-500 text-sm md:text-base col-span-full">
            Loading stats...
          </p>
        ) : (
          <>
            {/* Stock (Total) Card */}
            <div
              onClick={() => navigate('/inventory/commoditystocklist')}
              className="group relative bg-gradient-to-br from-amber-500 to-orange-600 text-white p-2.5 rounded-xl shadow-lg shadow-orange-900/20 border border-white/10 cursor-pointer hover:shadow-2xl hover:shadow-orange-900/30 hover:-translate-y-1 transition-all duration-300 overflow-hidden"
            >
              <div className="absolute top-0 right-0 -mt-2 -mr-2 w-16 h-16 bg-white opacity-10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500"></div>
              <div className="flex flex-col justify-between h-full relative z-10">
                <div>
                  <p className="text-amber-100 text-[9px] font-bold uppercase tracking-widest mb-0.5">
                    Stock (Total)
                  </p>
                  <p className="text-lg font-bold mt-0.5 tracking-tight group-hover:scale-105 transition-transform origin-left">
                    {parseQty(stats?.stocks?.totalQty).toFixed(1)} MT
                  </p>
                </div>
              </div>
            </div>

            {/* Total Warehouses Card */}
            <div
              onClick={() => navigate('/warehouse')}
              className="group relative bg-gradient-to-br from-blue-600 to-indigo-700 text-white p-2.5 rounded-xl shadow-lg shadow-blue-900/20 border border-white/10 cursor-pointer hover:shadow-2xl hover:shadow-blue-900/30 hover:-translate-y-1 transition-all duration-300 overflow-hidden"
            >
              <div className="absolute top-0 right-0 -mt-2 -mr-2 w-16 h-16 bg-white opacity-10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500"></div>
              <div className="flex flex-col justify-between h-full relative z-10">
                <div>
                  <p className="text-blue-100 text-[9px] font-bold uppercase tracking-widest mb-0.5">
                    Total Warehouses
                  </p>
                  <p className="text-lg font-bold mt-0.5 tracking-tight group-hover:scale-105 transition-transform origin-left">
                    {stats?.warehouses?.total || 0}
                  </p>
                </div>
              </div>
            </div>

            {/* Blocked Stock Card */}
            <div
              onClick={() => navigate('/stock-blocking/get-all')}
              className="group relative bg-gradient-to-br from-rose-500 to-pink-600 text-white p-2.5 rounded-xl shadow-lg shadow-rose-900/20 border border-white/10 cursor-pointer hover:shadow-2xl hover:shadow-rose-900/30 hover:-translate-y-1 transition-all duration-300 overflow-hidden"
            >
              <div className="absolute top-0 right-0 -mt-2 -mr-2 w-16 h-16 bg-white opacity-10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500"></div>
              <div className="flex flex-col justify-between h-full relative z-10">
                <div>
                  <p className="text-rose-100 text-[9px] font-bold uppercase tracking-widest mb-0.5">
                    Blocked Stock
                  </p>
                  <p className="text-lg font-bold mt-0.5 tracking-tight group-hover:scale-105 transition-transform origin-left">
                    {parseQty(stats?.blocked?.totalQty).toFixed(1)} MT
                  </p>
                </div>
                <div className="mt-1.5 pt-1.5 border-t border-rose-400 border-opacity-30 flex gap-4">
                  <div className="flex items-center gap-1.5">
                    <span className="text-rose-100 text-[9px] font-medium">PSS:</span>
                    <span className="font-bold text-[10px] text-white">{parseQty(stats?.blocked?.pssQty).toFixed(1)} MT</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-rose-100 text-[9px] font-medium">PSF:</span>
                    <span className="font-bold text-[10px] text-white">{parseQty(stats?.blocked?.psfQty).toFixed(1)} MT</span>
                  </div>
                </div>
              </div>
            </div>

          </>
        )}
      </div>

      {/* === Season Filter Buttons === */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Season:</span>
        <button
          onClick={() => setSelectedSeason('')}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all duration-200 ${selectedSeason === ''
            ? 'bg-gradient-to-r from-gray-800 to-gray-900 text-white shadow-lg shadow-gray-900/30 scale-105'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-md dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
            }`}
        >
          All
        </button>
        {seasons.map((season) => (
          <button
            key={season.id}
            onClick={() => setSelectedSeason(season.id)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all duration-200 ${selectedSeason === season.id
              ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-500/40 scale-105'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-md dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
              }`}
          >
            {season.code}
          </button>
        ))}
      </div>

      {/* === Row 1: Commodity wise Stock (Pulses) + (Oil Seed) === */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Commodity wise Stock (Pulses) Bar Chart Card */}
        <div className="bg-white dark:bg-gray-800 p-3 rounded-2xl shadow-lg shadow-emerald-900/5 border border-gray-100 dark:border-gray-700 flex flex-col w-full relative overflow-hidden group">
          <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-emerald-400 to-teal-500"></div>
          <h3 className="text-base font-bold mb-2 text-gray-800 dark:text-white flex items-center gap-2">
            <span className="w-1.5 h-6 bg-emerald-500 rounded-full"></span>
            Commodity wise Stock (Pulses)
          </h3>
          {barLoading ? (
            <div className="flex items-center justify-center h-[280px]">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-500"></div>
            </div>
          ) : barData.length === 0 ? (
            <div className="flex items-center justify-center h-[280px] text-gray-400">
              No data available
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart
                data={barData}
                margin={{ top: 5, right: 5, bottom: 15, left: 0 }}
              >
                <defs>
                  <linearGradient id="colorGradientPulses" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#34D399" stopOpacity={1} />
                    <stop offset="100%" stopColor="#059669" stopOpacity={0.8} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 9, fill: '#6B7280' }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 9, fill: '#6B7280' }}
                />
                <Tooltip
                  cursor={{ fill: 'transparent' }}
                  formatter={(value, name) => [`${value.toFixed(1)} MT`, name]}
                  contentStyle={{
                    backgroundColor: '#FFFFFF',
                    border: 'none',
                    borderRadius: '12px',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                    padding: '12px',
                    color: '#1F2937'
                  }}
                  itemStyle={{ color: '#059669', fontWeight: 600 }}
                  labelStyle={{ color: '#374151', fontWeight: 'bold', marginBottom: '4px' }}
                />
                <Bar
                  dataKey="totalQty"
                  fill="url(#colorGradientPulses)"
                  radius={[8, 8, 0, 0]}
                  barSize={25}
                  animationDuration={1500}
                  name="Available Qty"
                />
                <Bar
                  dataKey="totalBlockedQty"
                  fill="#EF4444"
                  radius={[8, 8, 0, 0]}
                  barSize={25}
                  animationDuration={1500}
                  name="Blocked Qty"
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Commodity wise Stock (Oil Seed) Chart Card */}
        <div className="bg-white dark:bg-gray-800 p-3 rounded-2xl shadow-lg shadow-amber-900/5 border border-gray-100 dark:border-gray-700 flex flex-col w-full relative overflow-hidden group">
          <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-amber-400 to-orange-500"></div>
          <h3 className="text-base font-bold mb-2 text-gray-800 dark:text-white flex items-center gap-2">
            <span className="w-1.5 h-6 bg-amber-500 rounded-full"></span>
            Commodity wise Stock (Oil Seed)
          </h3>
          {oilSeedLoading ? (
            <div className="flex items-center justify-center h-[300px]">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
            </div>
          ) : oilSeedData.length === 0 ? (
            <div className="flex items-center justify-center h-[300px] text-gray-400">
              No data available
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart
                data={oilSeedData}
                margin={{ top: 10, right: 5, bottom: 30, left: 0 }}
              >
                <defs>
                  <linearGradient id="colorGradientOilSeed" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#F59E0B" stopOpacity={1} />
                    <stop offset="100%" stopColor="#F59E0B" stopOpacity={0.2} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 9, fill: '#6B7280' }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  dy={10}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 9, fill: '#6B7280' }}
                  label={{ value: 'Quantity (MT)', angle: -90, position: 'insideLeft', fill: '#9CA3AF', fontSize: 9 }}
                />
                <Tooltip
                  cursor={{ fill: 'transparent' }}
                  formatter={(value, name) => [`${parseFloat(value || 0).toFixed(1)} MT`, name]}
                  contentStyle={{
                    backgroundColor: '#FFFFFF',
                    border: 'none',
                    borderRadius: '12px',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                    padding: '12px',
                    color: '#1F2937'
                  }}
                  itemStyle={{ color: '#B45309', fontWeight: 600 }}
                  labelStyle={{ color: '#374151', fontWeight: 'bold', marginBottom: '4px' }}
                />
                <Bar
                  dataKey="totalQty"
                  fill="url(#colorGradientOilSeed)"
                  radius={[8, 8, 0, 0]}
                  barSize={45}
                  animationDuration={1500}
                  name="Available Qty"
                />
                <Bar
                  dataKey="totalBlockedQty"
                  fill="#EF4444"
                  radius={[8, 8, 0, 0]}
                  barSize={45}
                  animationDuration={1500}
                  name="Blocked Qty"
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* === Row 2: Stock Distribution + Warehouses Map === */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Stock Distribution Pie Chart Card */}
        <div className="bg-white dark:bg-gray-800 p-3 rounded-2xl shadow-lg shadow-rose-900/5 border border-gray-100 dark:border-gray-700 flex flex-col w-full relative overflow-hidden group">
          <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-rose-400 to-red-500"></div>
          <h3 className="text-base font-bold mb-2 text-gray-800 dark:text-white flex items-center gap-2">
            <span className="w-1.5 h-6 bg-rose-500 rounded-full"></span>
            Stock Distribution
          </h3>
          {loading ? (
            <div className="flex items-center justify-center h-[240px]">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-rose-500"></div>
            </div>
          ) : stats?.stocks ? (
            <div className="flex flex-col h-full">
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie
                    data={[
                      { name: 'PSS Quantity', value: parseQty(stats?.stocks?.pssQty) },
                      { name: 'PSF Quantity', value: parseQty(stats?.stocks?.psfQty) },
                    ]}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={85}
                    paddingAngle={5}
                    dataKey="value"
                    cornerRadius={6}
                  >
                    <Cell fill="#F43F5E" /> {/* Rose 500 */}
                    <Cell fill="#FB923C" /> {/* Orange 400 */}
                  </Pie>
                  <Tooltip
                    formatter={(value, name) => [`${parseFloat(value || 0).toFixed(1)} MT`, name]}
                    contentStyle={{
                      backgroundColor: '#FFFFFF',
                      border: 'none',
                      borderRadius: '12px',
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                      padding: '12px',
                      color: '#1F2937'
                    }}
                    itemStyle={{ color: '#1F2937', fontWeight: 600 }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex justify-center gap-4 mt-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#F43F5E]"></div>
                  <span className="text-gray-600 dark:text-gray-300">PSS Qty: <span className="font-bold">{parseQty(stats?.stocks?.pssQty).toFixed(1)} MT</span></span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#FB923C]"></div>
                  <span className="text-gray-600 dark:text-gray-300">PSF Qty: <span className="font-bold">{parseQty(stats?.stocks?.psfQty).toFixed(1)} MT</span></span>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-[240px] text-gray-400">
              No stock data available
            </div>
          )}
        </div>

        {/* === Aged Warehouses Table === */}
        < div className="relative bg-white dark:bg-gray-800 p-3 dark:text-white rounded-2xl shadow-lg shadow-gray-900/5 hover:shadow-xl transition-all duration-300 flex flex-col w-full min-h-[350px] border border-gray-100 dark:border-gray-700 overflow-hidden group" >
          <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-purple-400 to-pink-500"></div>
          <div className="flex flex-col gap-2 mb-3">
            <h2 className="text-base font-bold text-gray-800 dark:text-white flex items-center gap-2">
              <span className="w-1.5 h-6 bg-purple-500 rounded-full"></span>
              Aged Warehouses (Age {'>'}90 days)
            </h2>

            {/* Filter Dropdowns */}
            <div className="flex flex-col sm:flex-row gap-3">
              {/* State Filter */}
              <div className="relative flex-1">
                <select
                  value={agedStateFilter}
                  onChange={(e) => setAgedStateFilter(e.target.value)}
                  className="w-full px-4 py-2 pl-10 pr-10 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all appearance-none cursor-pointer truncate"
                >
                  <option value="">All States</option>
                  {statesList.map((state) => (
                    <option key={state.id} value={state.id}>
                      {state.name}
                    </option>
                  ))}
                </select>
                <svg
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
                </svg>
                <svg
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              {/* Warehouse Filter */}
              <div className="relative flex-1">
                <select
                  value={agedSearchText}
                  onChange={(e) => setAgedSearchText(e.target.value)}
                  className="w-full px-4 py-2 pl-10 pr-10 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all appearance-none cursor-pointer truncate"
                >
                  <option value="">All Warehouses</option>
                  {warehousesList.map((warehouse) => (
                    <option key={warehouse.id} value={warehouse.id}>
                      {warehouse.warehouse_name}
                    </option>
                  ))}
                </select>
                <svg
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
                </svg>
                <svg
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>

          {
            agedWarehousesLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-500"></div>
              </div>
            ) : agedWarehousesData.length === 0 ? (
              <div className="flex items-center justify-center h-full text-gray-400">
                No aged warehouses data available
              </div>
            ) : (
              <>
                {/* Table */}
                <div className="overflow-x-auto flex-1 rounded-xl border border-gray-100 dark:border-gray-700/50">
                  <table className="min-w-full border-collapse text-left">
                    <thead>
                      <tr className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-b border-gray-100 dark:border-gray-700">
                        <th className="px-3 py-2 text-[10px] font-semibold text-gray-700 uppercase tracking-wider dark:text-gray-300">
                          Warehouse
                        </th>
                        <th className="px-3 py-2 text-[10px] font-semibold text-gray-700 uppercase tracking-wider dark:text-gray-300 text-center whitespace-nowrap">
                          Total Qty (MT)
                        </th>
                        <th className="px-3 py-2 text-[10px] font-semibold text-gray-700 uppercase tracking-wider dark:text-gray-300 text-center whitespace-nowrap">
                          Avg Age (Days)
                        </th>
                        <th className="px-3 py-2 text-[10px] font-semibold text-gray-700 uppercase tracking-wider dark:text-gray-300 text-center whitespace-nowrap">
                          WHR Count
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {agedWarehousesData
                        .slice(
                          (agedCurrentPage - 1) * agedPageSize,
                          agedCurrentPage * agedPageSize
                        )
                        .map((warehouse, index) => (
                          <tr
                            key={warehouse.warehouseId || index}
                            className="group hover:bg-gradient-to-r hover:from-purple-50/50 hover:to-pink-50/50 dark:hover:from-purple-900/10 dark:hover:to-pink-900/10 transition-all duration-200"
                          >
                            <td className="px-3 py-2 text-xs font-medium text-gray-900 dark:text-white">
                              <div className="flex items-center gap-1.5">
                                <div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 group-hover:scale-125 transition-transform flex-shrink-0"></div>
                                <span className="line-clamp-1" title={warehouse.warehouseName}>
                                  {warehouse.warehouseName}
                                </span>
                              </div>
                            </td>
                            <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-300 text-center">
                              <span className="font-semibold whitespace-nowrap">
                                {warehouse.totalQty.toLocaleString()}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-300 text-center">
                              <span className={`inline-flex items-center px-1.5 py-0.5 rounded font-semibold whitespace-nowrap ${warehouse.avgAgeInDays > 365
                                ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                                : warehouse.avgAgeInDays > 180
                                  ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300'
                                  : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                                }`}>
                                {warehouse.avgAgeInDays}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-300 text-center">
                              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold text-[10px] shadow-sm">
                                {warehouse.whrCount}
                              </span>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Controls */}
                <div className="flex flex-col sm:flex-row justify-between items-center mt-4 pt-2 gap-3 sm:gap-0">
                  <p className="text-xs text-gray-500 dark:text-gray-400 order-2 sm:order-1">
                    Showing <span className="font-medium text-gray-900 dark:text-white">{(agedCurrentPage - 1) * agedPageSize + 1}</span>-
                    <span className="font-medium text-gray-900 dark:text-white">{Math.min(agedCurrentPage * agedPageSize, agedWarehousesData.length)}</span> of{' '}
                    <span className="font-medium text-gray-900 dark:text-white">{agedWarehousesData.length}</span>
                  </p>

                  <div className="flex items-center gap-2 order-1 sm:order-2 w-full sm:w-auto justify-between sm:justify-end">
                    <button
                      onClick={() => setAgedCurrentPage((prev) => Math.max(prev - 1, 1))}
                      disabled={agedCurrentPage === 1}
                      className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gradient-to-r hover:from-purple-500 hover:to-pink-500 hover:text-white hover:border-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-all text-gray-600 dark:text-gray-300"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 px-3">
                      {agedCurrentPage} / {agedTotalPages}
                    </span>
                    <button
                      onClick={() => setAgedCurrentPage((prev) => Math.min(prev + 1, agedTotalPages))}
                      disabled={agedCurrentPage === agedTotalPages}
                      className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gradient-to-r hover:from-purple-500 hover:to-pink-500 hover:text-white hover:border-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-all text-gray-600 dark:text-gray-300"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </>
            )
          }
        </div >
      </div>

      {/* === Row 3: State-wise Stock Table + Aged Warehouses === */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">

        {/* === State-wise Stock Table === */}
        < div className="relative bg-white dark:bg-gray-800 p-3 dark:text-white rounded-2xl shadow-lg shadow-gray-900/5 hover:shadow-xl transition-all duration-300 flex flex-col w-full min-h-[450px] border border-gray-100 dark:border-gray-700 overflow-hidden group" >
          <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-blue-400 to-cyan-500"></div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
            <h2 className="text-base font-bold text-gray-800 dark:text-white flex items-center gap-2">
              <span className="w-1.5 h-6 bg-blue-500 rounded-full"></span>
              State wise Stock
            </h2>

            {/* State Filter Dropdown */}
            <div className="relative w-full sm:w-64">
              <select
                value={stateSearchText}
                onChange={(e) => setStateSearchText(e.target.value)}
                className="w-full px-4 py-2 pl-10 pr-10 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all appearance-none cursor-pointer truncate"
              >
                <option value="">All States</option>
                {statesList.map((state) => (
                  <option key={state.id} value={state.id}>
                    {state.name}
                  </option>
                ))}
              </select>
              <svg
                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
              </svg>
              <svg
                className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>

          {
            tableLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
              </div>
            ) : tableData.length === 0 ? (
              <div className="flex items-center justify-center h-full text-gray-400">
                No state-wise stock data available
              </div>
            ) : (
              <>
                {/* Table */}
                <div className="overflow-x-auto flex-1 rounded-xl border border-gray-100 dark:border-gray-700/50">
                  <table className="min-w-full border-collapse text-left">
                    <thead>
                      <tr className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border-b border-gray-100 dark:border-gray-700">
                        <th className="px-3 py-2 text-[10px] font-semibold text-gray-700 uppercase tracking-wider dark:text-gray-300">
                          State
                        </th>
                        <th className="px-3 py-2 text-[10px] font-semibold text-gray-700 uppercase tracking-wider dark:text-gray-300 text-center">
                          Total Qty (MT)
                        </th>
                        <th className="px-3 py-2 text-[10px] font-semibold text-gray-700 uppercase tracking-wider dark:text-gray-300 text-center">
                          Blocked Qty (MT)
                        </th>
                        <th className="px-3 py-2 text-[10px] font-semibold text-gray-700 uppercase tracking-wider dark:text-gray-300 text-center">
                          Available Qty (MT)
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {table
                        .getRowModel()
                        .rows.slice(
                          (currentPage - 1) * pageSize,
                          currentPage * pageSize
                        )
                        .map((row) => (
                          <tr
                            key={row.id}
                            className="group hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-cyan-50/50 dark:hover:from-blue-900/10 dark:hover:to-cyan-900/10 transition-all duration-200"
                          >
                            <td className="px-3 py-2 text-xs font-medium text-gray-900 dark:text-white">
                              <div className="flex items-center gap-1.5">
                                <div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 group-hover:scale-125 transition-transform"></div>
                                <span>{row.original.name}</span>
                              </div>
                            </td>
                            <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-300 text-center">
                              <span className="font-semibold">
                                {parseFloat(row.original.totalQty || 0).toFixed(1)}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-300 text-center">
                              <span className="font-semibold text-red-600 dark:text-red-400">
                                {parseFloat(row.original.blockedQty || 0).toFixed(1)}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-300 text-center">
                              <span className="font-semibold text-green-600 dark:text-green-400">
                                {parseFloat(row.original.availableQty || 0).toFixed(1)}
                              </span>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>

                {/* Summary Stats */}
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-1 2xl:grid-cols-3 gap-1.5">
                    <div className="text-center p-1.5 rounded-lg bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20">
                      <p className="text-[9px] uppercase tracking-wider text-gray-600 dark:text-gray-400 font-bold mb-0.5">Total</p>
                      <p className="text-xs font-bold text-blue-600 dark:text-blue-400 break-words leading-tight">
                        {tableData.reduce((sum, state) => sum + parseFloat(state.totalQty || 0), 0).toFixed(1)} MT
                      </p>
                    </div>
                    <div className="text-center p-1.5 rounded-lg bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20">
                      <p className="text-[9px] uppercase tracking-wider text-gray-600 dark:text-gray-400 font-bold mb-0.5">Blocked</p>
                      <p className="text-xs font-bold text-red-600 dark:text-red-400 break-words leading-tight">
                        {tableData.reduce((sum, state) => sum + parseFloat(state.blockedQty || 0), 0).toFixed(1)} MT
                      </p>
                    </div>
                    <div className="text-center p-1.5 rounded-lg bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20">
                      <p className="text-[9px] uppercase tracking-wider text-gray-600 dark:text-gray-400 font-bold mb-0.5">Available</p>
                      <p className="text-xs font-bold text-green-600 dark:text-green-400 break-words leading-tight">
                        {tableData.reduce((sum, state) => sum + parseFloat(state.availableQty || 0), 0).toFixed(1)} MT
                      </p>
                    </div>
                  </div>
                </div>

                {/* Pagination Controls */}
                <div className="flex flex-col sm:flex-row justify-between items-center mt-4 pt-2 gap-3 sm:gap-0">
                  <p className="text-xs text-gray-500 dark:text-gray-400 order-2 sm:order-1">
                    Showing <span className="font-medium text-gray-900 dark:text-white">{(currentPage - 1) * pageSize + 1}</span>-
                    <span className="font-medium text-gray-900 dark:text-white">{Math.min(currentPage * pageSize, tableData.length)}</span> of{' '}
                    <span className="font-medium text-gray-900 dark:text-white">{tableData.length}</span>
                  </p>

                  <div className="flex items-center gap-2 order-1 sm:order-2 w-full sm:w-auto justify-between sm:justify-end">
                    <button
                      onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gradient-to-r hover:from-blue-500 hover:to-cyan-500 hover:text-white hover:border-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-all text-gray-600 dark:text-gray-300"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 px-3">
                      {currentPage} / {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gradient-to-r hover:from-blue-500 hover:to-cyan-500 hover:text-white hover:border-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-all text-gray-600 dark:text-gray-300"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </>
            )
          }
        </div >

        {/* Backdrop for fullscreen */}
        {isMapFullscreen && (
          <div
            onClick={toggleFullscreen}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998] transition-opacity"
          />
        )}

        {/* India State Map with Filter */}
        <div
          className={`bg-white dark:bg-gray-800 shadow-lg shadow-blue-900/5 hover:shadow-xl flex flex-col border border-gray-100 dark:border-gray-700 overflow-hidden group ${
            isMapFullscreen
              ? 'fixed inset-0 z-[9999] m-2 sm:m-4 rounded-2xl h-[calc(100vh-1rem)] sm:h-[calc(100vh-2rem)] w-[calc(100vw-1rem)] sm:w-[calc(100vw-2rem)] p-3 sm:p-4'
              : 'relative w-full rounded-2xl p-4'
          }`}
        >
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-400 to-indigo-500"></div>
          <div className="flex justify-between items-center mb-3 flex-shrink-0">
            <div className="flex items-center gap-2">
              <span className="w-2 h-7 bg-blue-500 rounded-full"></span>
              <div>
                <h3 className="text-base sm:text-lg font-bold text-gray-800 dark:text-white leading-tight">
                  Warehouses Map
                  {selectedState && (
                    <span className="text-xs sm:text-sm font-normal text-blue-600 dark:text-blue-400 ml-2">
                      ({getSelectedStateName()})
                    </span>
                  )}
                </h3>
                {isMapFullscreen && (
                  <p className="text-[11px] text-gray-500 dark:text-gray-400 hidden sm:block">
                    Interactive Map & State Summary Table
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              {selectedState && (
                <button
                  onClick={() => setSelectedState('')}
                  className="px-2.5 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 rounded-lg transition flex items-center gap-1.5"
                  title="Back to India Map"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span className="hidden sm:inline">India Map</span>
                </button>
              )}

              <button
                onClick={toggleFullscreen}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg transition"
                title={isMapFullscreen ? 'Exit Fullscreen (Esc)' : 'Fullscreen'}
              >
                {isMapFullscreen ? (
                  <Minimize2 className="w-5 h-5" />
                ) : (
                  <Maximize2 className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>

          {/* Body Content */}
          {isMapFullscreen ? (
            /* Split 50/50 Screen Layout in Expanded View */
            <div className="flex-1 flex flex-col lg:flex-row gap-3 sm:gap-4 min-h-0 overflow-hidden">
              {/* Left Column: Interactive Map (50%) */}
              <div className="w-full lg:w-1/2 h-[320px] lg:h-full flex flex-col rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 relative bg-slate-50 dark:bg-gray-900">
                {renderMapContent()}
              </div>

              {/* Right Column: State-wise Warehouses & Stock Table (50%) */}
              <div className="w-full lg:w-1/2 h-full flex flex-col rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/90 overflow-hidden shadow-sm min-h-0">
                {/* Search & Header Controls */}
                <div className="p-3 bg-gray-50/90 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div>
                    <h4 className="text-xs sm:text-sm font-bold text-gray-800 dark:text-white flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                      State-wise Warehouses & Stock
                    </h4>
                    <p className="text-[10px] sm:text-[11px] text-gray-500 dark:text-gray-400">
                      Showing {filteredExpandedStates.length} of {expandedStatesTableData.length} active states
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    {selectedState && (
                      <button
                        onClick={() => setSelectedState('')}
                        className="px-2 py-1 text-[11px] bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-md font-medium hover:bg-blue-200 transition-colors flex items-center gap-1"
                        title="Clear State Filter"
                      >
                        <span>{getSelectedStateName()}</span>
                        <span className="text-xs">✕</span>
                      </button>
                    )}
                    <div className="relative w-full sm:w-44">
                      <input
                        type="text"
                        placeholder="Search state..."
                        value={expandedSearchText}
                        onChange={(e) => setExpandedSearchText(e.target.value)}
                        className="w-full pl-7 pr-6 py-1 text-xs bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-800 dark:text-white"
                      />
                      <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                      {expandedSearchText && (
                        <button
                          onClick={() => setExpandedSearchText('')}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xs"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Table Area */}
                <div className="flex-1 overflow-y-auto min-h-0">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-left">
                    <thead className="bg-gray-100 dark:bg-gray-700/80 sticky top-0 z-10">
                      <tr>
                        <th className="px-3 py-2 text-[10px] sm:text-[11px] font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider w-10 text-center">
                          #
                        </th>
                        <th className="px-3 py-2 text-[10px] sm:text-[11px] font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                          State
                        </th>
                        <th className="px-3 py-2 text-[10px] sm:text-[11px] font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider text-center">
                          Warehouses
                        </th>
                        <th className="px-3 py-2 text-[10px] sm:text-[11px] font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider text-right">
                          Total Quantity (MT)
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60 bg-white dark:bg-gray-800">
                      {allStatesStockLoading || warehouseCountLoading ? (
                        <tr>
                          <td colSpan={4} className="text-center py-8 text-xs text-gray-400">
                            <div className="flex flex-col items-center gap-1.5">
                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                              <span>Loading table data...</span>
                            </div>
                          </td>
                        </tr>
                      ) : filteredExpandedStates.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="text-center py-8 text-xs text-gray-400">
                            No states found
                          </td>
                        </tr>
                      ) : (
                        filteredExpandedStates.map((row, idx) => {
                          const isSelected = selectedState === row.stateId;
                          return (
                            <tr
                              key={row.stateId || row.stateName}
                              onClick={() => {
                                if (isSelected) {
                                  setSelectedState('');
                                } else {
                                  setSelectedState(row.stateId);
                                }
                              }}
                              className={`cursor-pointer transition-colors duration-150 ${
                                isSelected
                                  ? 'bg-blue-50/90 dark:bg-blue-900/30 text-blue-900 dark:text-blue-100 font-medium'
                                  : 'hover:bg-gray-50 dark:hover:bg-gray-700/40 text-gray-700 dark:text-gray-300'
                              }`}
                              title={`Click to ${isSelected ? 'unselect' : 'view on map'}`}
                            >
                              <td className="px-3 py-2 text-xs text-center text-gray-500 dark:text-gray-400">
                                {idx + 1}
                              </td>
                              <td className="px-3 py-2 text-xs font-semibold">
                                <div className="flex items-center gap-2">
                                  <span
                                    className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                      isSelected
                                        ? 'bg-blue-600 ring-2 ring-blue-300'
                                        : 'bg-gray-300 dark:bg-gray-600'
                                    }`}
                                  />
                                  <span className={isSelected ? 'text-blue-600 dark:text-blue-400 font-bold' : ''}>
                                    {row.stateName}
                                  </span>
                                </div>
                              </td>
                              <td className="px-3 py-2 text-xs text-center">
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300">
                                  {row.warehouseCount}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-xs text-right font-semibold text-gray-900 dark:text-white">
                                {row.totalQty.toLocaleString('en-IN', {
                                  minimumFractionDigits: 1,
                                  maximumFractionDigits: 1,
                                })}{' '}
                                <span className="text-[10px] text-gray-500 dark:text-gray-400 font-normal">MT</span>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Summary Footer */}
                <div className="p-2.5 bg-gray-50 dark:bg-gray-700/40 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="p-1.5 rounded-lg bg-white dark:bg-gray-800 shadow-sm border border-gray-100 dark:border-gray-700">
                      <p className="text-[9px] uppercase font-bold text-gray-500 dark:text-gray-400">Total States</p>
                      <p className="text-xs sm:text-sm font-bold text-gray-800 dark:text-white mt-0.5">
                        {expandedTotals.totalStates}
                      </p>
                    </div>
                    <div className="p-1.5 rounded-lg bg-white dark:bg-gray-800 shadow-sm border border-gray-100 dark:border-gray-700">
                      <p className="text-[9px] uppercase font-bold text-blue-600 dark:text-blue-400">Warehouses</p>
                      <p className="text-xs sm:text-sm font-bold text-blue-600 dark:text-blue-400 mt-0.5">
                        {expandedTotals.totalWarehouses}
                      </p>
                    </div>
                    <div className="p-1.5 rounded-lg bg-white dark:bg-gray-800 shadow-sm border border-gray-100 dark:border-gray-700">
                      <p className="text-[9px] uppercase font-bold text-emerald-600 dark:text-emerald-400">Total Stock</p>
                      <p className="text-xs sm:text-sm font-bold text-emerald-600 dark:text-emerald-400 mt-0.5 truncate">
                        {expandedTotals.totalQty.toLocaleString('en-IN', {
                          minimumFractionDigits: 1,
                          maximumFractionDigits: 1,
                        })} MT
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Normal Card View (Unexpanded) */
            <div
              className="flex-1"
              style={{
                minHeight: '450px',
                backgroundColor: '#f8fafc',
              }}
            >
              {renderMapContent()}
            </div>
          )}
        </div>
      </div >
    </div >
  );
};

export default Dashboard;
